import { useOriginalData, useProcessedData } from "@/stores/data-processing";
import { useActiveDataflow, useActiveDataflowId } from "@/stores/dataflow";
import { useGeoJSONLayers } from "@/stores/map";
import { CodeHighlight } from "@mantine/code-highlight";
import "@mantine/code-highlight/styles.css";
import { Code, ScrollArea } from "@mantine/core";
import { useEffect, useState } from "react";

export function WorkspaceContentJson() {
  const activeDataflowId = useActiveDataflowId();
  const dataflow = useActiveDataflow();
  const geoJSONLayers = useGeoJSONLayers();
  const originalData = useOriginalData(activeDataflowId || "");
  const processedData = useProcessedData(activeDataflowId || "");
  const [jsonData, setJsonData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeDataflowId || !dataflow) {
      setJsonData(null);
      setError("No active dataflow");
      return;
    }

    try {
      const inputTransformer = dataflow.flow_definition_json?.find(
        (t) => t.type === "user_file" || t.type === "data_input"
      );

      let inputData = null;

      if (inputTransformer) {
        if (inputTransformer.type === "user_file") {
          const layerId = `geojson-${inputTransformer.file_id}`;
          const layerData = geoJSONLayers[layerId];

          if (layerData?.data) {
            inputData = {
              source: "geojson_file",
              file_id: inputTransformer.file_id,
              file_name: inputTransformer.file_name,
              file_type: inputTransformer.file_type,
              data: layerData.data,
            };
          } else if (originalData && originalData.length > 0) {
            inputData = {
              source: "user_file",
              file_id: inputTransformer.file_id,
              file_name: inputTransformer.file_name,
              file_type: inputTransformer.file_type,
              row_count: originalData.length,
              columns: originalData[0] ? Object.keys(originalData[0]) : [],
              data: originalData,
            };
          } else {
            inputData = {
              source: "user_file",
              file_id: inputTransformer.file_id,
              file_name: inputTransformer.file_name,
              file_type: inputTransformer.file_type,
              status: "loading",
            };
          }
        } else if (inputTransformer.type === "data_input") {
          if (originalData && originalData.length > 0) {
            inputData = {
              source: "discover_dataset",
              db_table_id: inputTransformer.db_table_id,
              row_count: originalData.length,
              columns: originalData[0] ? Object.keys(originalData[0]) : [],
              data: originalData,
            };
          } else {
            inputData = {
              source: "discover_dataset",
              db_table_id: inputTransformer.db_table_id,
              status: "loading",
            };
          }
        }
      }

      const jsonRepresentation = {
        dataflow_id: dataflow.dataflow_id,
        name: dataflow.dataflow_name,
        description: dataflow.dataflow_description,
        project_id: dataflow.project_id,
        flow_definition_json: dataflow.flow_definition_json,
        metadata: {
          created_at: dataflow.datetime_created,
          updated_at: dataflow.datetime_updated,
          transformers_count: dataflow.flow_definition_json?.length || 0,
        },
        input_data: inputData,
        processed_data: processedData
          ? {
              row_count: processedData.data?.length || 0,
              columns: processedData.metadata?.columns || [],
              data: processedData.data || [],
            }
          : null,
      };

      setJsonData(jsonRepresentation);
      setError(null);
    } catch (err) {
      setError(
        `Error formatting JSON: ${
          err instanceof Error ? err.message : "Unknown error"
        }`
      );
      setJsonData(null);
    }
  }, [activeDataflowId, dataflow, geoJSONLayers, originalData, processedData]);

  if (error) {
    return (
      <div
        style={{
          height: "calc(100vh - 160px)",
          flex: 1,
          padding: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Code color="red" style={{ fontSize: "14px" }}>
          {error}
        </Code>
      </div>
    );
  }

  if (!jsonData) {
    return (
      <div
        style={{
          height: "calc(100vh - 160px)",
          flex: 1,
          padding: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Code color="gray" style={{ fontSize: "14px" }}>
          No dataflow selected
        </Code>
      </div>
    );
  }

  return (
    <div
      style={{
        height: "calc(100vh - 160px)",
        flex: 1,
        backgroundColor: "#F7F8FB",
      }}
    >
      <ScrollArea h="100%" w="100%">
        <CodeHighlight
          code={JSON.stringify(jsonData, null, 2)}
          lang="json"
          background="#F7F8FB"
          p="sm"
          pb={100}
          style={{
            fontSize: "13px",
            fontFamily: "Monaco, 'Courier New', monospace",
            lineHeight: "1.5",
          }}
        />
      </ScrollArea>
    </div>
  );
}
